import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import { ThumbUp, ThumbDown, Fullscreen, Replay } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CopyButton } from '../CopyButton/CopyButton';
import { useFeedbackSubmission } from '../../services/Feedback/feedbackApi';
import { StyledIconButton } from './Chat.styles';
import { useSession } from '../../context/SessionContext';
import { STORAGE_KEYS } from '../../constants/storage';

interface MessageActionsProps {
  messageId: string;
  content: string;
  hasPlotly?: boolean;
  traceId: string;
  onFullscreen?: () => void;
  isError?: boolean;
  onRetry?: () => void;
  question?: string;
}
const MAX_CHARS = 3000;
const FEEDBACK_STORAGE_PREFIX = STORAGE_KEYS.MESSAGE_FEEDBACK;

export const MessageActions: React.FC<MessageActionsProps> = ({ messageId, content, hasPlotly, onFullscreen, traceId, isError, onRetry, question }) => {
  const { t } = useTranslation();
  const { sessionId } = useSession();
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { submitFeedback } = useFeedbackSubmission();
  const [comment, setComment] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempFeedback, setTempFeedback] = useState<'up' | 'down' | null>(null);
  const [animateUp, setAnimateUp] = useState(false);
  const [animateDown, setAnimateDown] = useState(false);

  // Load saved feedback state on component mount
  useEffect(() => {
    const savedFeedback = localStorage.getItem(`${FEEDBACK_STORAGE_PREFIX}${messageId}`);
    if (savedFeedback) {
      try {
        const parsedFeedback = JSON.parse(savedFeedback);
        setFeedback(parsedFeedback.type);
      } catch (error) {
        console.error('Error parsing saved feedback:', error);
      }
    }
  }, [messageId]);

  // Reset animation classes after animation completes
  useEffect(() => {
    if (animateUp) {
      const timer = setTimeout(() => setAnimateUp(false), 500);
      return () => clearTimeout(timer);
    }
  }, [animateUp]);

  useEffect(() => {
    if (animateDown) {
      const timer = setTimeout(() => setAnimateDown(false), 500);
      return () => clearTimeout(timer);
    }
  }, [animateDown]);

  // Save feedback to localStorage whenever it changes
  useEffect(() => {
    if (feedback) {
      localStorage.setItem(
        `${FEEDBACK_STORAGE_PREFIX}${messageId}`, 
        JSON.stringify({ 
          type: feedback, 
          timestamp: new Date().toISOString() 
        })
      );
    }
  }, [feedback, messageId]);

  const handleFeedbackClick = (type: 'up' | 'down') => {
    // Set animation state
    if (type === 'up') {
      setAnimateUp(true);
    } else {
      setAnimateDown(true);
    }
    
    // If clicking the same feedback type that's already selected, don't open dialog
    if (feedback === type) {
      return;
    }
    
    setTempFeedback(type);
    setDialogOpen(true);
  };

  const handleSubmitFeedback = async () => {
    if (!tempFeedback) return;
    
    try {
      setIsSubmitting(true);
      
      await submitFeedback(
        traceId,
        sessionId || '',
        tempFeedback === 'up' ? 1 : 0,
        comment,
        content,
        question
      );
      
      // Only set feedback after successful submission
      setFeedback(tempFeedback);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
      setDialogOpen(false);
      setComment('');
    }
  };

  const handleCancelFeedback = () => {
    setDialogOpen(false);
    setComment('');
    // No automatic feedback submission on cancel
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
        <Box display="flex" alignItems="center" gap={0.5}>
          {isError && onRetry && (
            <Tooltip 
              title={t('retryMessage')}
              PopperProps={{
                disablePortal: false,
                sx: { zIndex: 99999999 }
              }}
            >
              <StyledIconButton
                size="small"
                onClick={onRetry}
                color="error"
              >
                <Replay fontSize="small" />
              </StyledIconButton>
            </Tooltip>
          )}
          
          <Tooltip 
            title={feedback === 'up' ? t('feedbackSubmitted') : t('thumbsUp')}
            PopperProps={{
              disablePortal: false,
              sx: { zIndex: 99999999 }
            }}
          >
            <StyledIconButton 
              size="small" 
              onClick={() => handleFeedbackClick('up')}
              disabled={isSubmitting}
              color={feedback === 'up' ? 'primary' : 'default'}
              className={animateUp ? 'feedback-clicked' : ''}
              sx={{
                '&:hover': {
                  color: '#4caf50', // Green color on hover
                },
                ...(feedback === 'up' && {
                  color: '#4caf50', // Green color when selected
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                }),
                ...(animateUp && {
                  color: '#4caf50', // Green color during animation
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                }),
              }}
            >
              <ThumbUp fontSize="small" />
            </StyledIconButton>
          </Tooltip>
          
          <Tooltip 
            title={feedback === 'down' ? t('feedbackSubmitted') : t('thumbsDown')}
            PopperProps={{
              disablePortal: false,
              sx: { zIndex: 99999999 }
            }}
          >
            <StyledIconButton 
              size="small" 
              onClick={() => handleFeedbackClick('down')}
              disabled={isSubmitting}
              color={feedback === 'down' ? 'error' : 'default'}
              className={animateDown ? 'feedback-clicked' : ''}
              sx={{
                '&:hover': {
                  color: '#f44336', // Red color on hover
                },
                ...(feedback === 'down' && {
                  color: '#f44336', // Red color when selected
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                }),
                ...(animateDown && {
                  color: '#f44336', // Red color during animation
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                }),
              }}
            >
              <ThumbDown fontSize="small" />
            </StyledIconButton>
          </Tooltip>

          <CopyButton text={content} />
        </Box>

        {hasPlotly && (
          <Tooltip 
            title="View fullscreen"
            PopperProps={{
              disablePortal: false,
              sx: { zIndex: 99999999 }
            }}
          >
            <StyledIconButton
              size="small"
              onClick={onFullscreen}
              sx={{ opacity: 0.7 }}
            >
              <Fullscreen fontSize="small" />
            </StyledIconButton>
          </Tooltip>
        )}
      </Box>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCancelFeedback} 
        sx={{ 
          zIndex: 1000000000000,
          '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '90vw',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>
          {tempFeedback === 'up' ? t('provideFeedbackPositive') : t('provideFeedbackNegative')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            multiline
            rows={4}
            placeholder={t('feedbackPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            slotProps={{ htmlInput: { maxLength: MAX_CHARS } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelFeedback} disabled={isSubmitting}>{t('cancel')}</Button>
          <Button 
            onClick={handleSubmitFeedback} 
            variant="contained"
            color={tempFeedback === 'up' ? 'success' : 'error'}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('submitting') : t('submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 